/**
 * Item 4b/4c (2026-07-21): the spend-category cell, extracted out of
 * LineItemsEditor.jsx so its own debounced-suggestion state/effect can be
 * scoped per-row (React hooks can't be called conditionally/in a loop
 * inside LineItemsEditor's own row .map(), so each row needs to be its own
 * component instance - same reason DatePickerField is its own component).
 *
 * Three dropdown tiers, in order: Used in this RFP (categories chosen on
 * other lines - the buyer's own prior categorization in this RFP, a
 * stronger signal than keyword scoring, so it ranks first per Ashok's
 * ruling 2026-07-21), Suggested (ranked, from GET /categories/suggest,
 * debounced on description change), All categories (the full,
 * unrestricted, search-filtered list - Defect B). Selection from any tier
 * is equally valid; tiers are ordering, never filtering. A non-blocking
 * amber warning (Item 4c) renders alongside the hard-validation FieldError
 * when the chosen category shares no L1 branch with the rest of the RFP.
 *
 * The dropdown's content/scroll structure is composed directly from Radix
 * primitives rather than the shared '@/components/ui/select' SelectContent
 * wrapper (2026-07-21 UX fixes) - needed for two things unique to this
 * dropdown that would otherwise require changing the shared primitive for
 * every other Select in the app:
 *   1. A sticky search box - a non-scrolling sibling ABOVE the scrollable
 *      list, not a child of it, so it's never lost mid-scroll.
 *   2. Controlled-speed hover-scroll buttons - Radix's built-in
 *      ScrollUpButton/ScrollDownButton auto-scroll roughly once per
 *      animation frame, so the rate scales with the monitor's refresh
 *      rate and felt "far too fast"; a time-based setInterval gives a
 *      steady, frame-rate-independent speed instead.
 * SelectItem/SelectGroup/SelectLabel (the option-list primitives) are
 * still the shared ones - only the outer content/scroll shell is custom.
 */
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Input } from '@/components/ui/input';
import { Select, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import api from '@/lib/api';
import FieldError from '@/components/FieldError';
import CodeNameLabel from '@/components/CodeNameLabel';
import { usedCategoryCodes, buildCategoryTiers } from '@/lib/categoryTiers';
import { getCategoryConsistencyWarning } from '@/lib/categoryConsistency';
import {
  readCategoryNoFit,
  categoryNoFitRequestNumber,
  AWAITING_MASTER_UPDATE,
  NO_FIT_FIELD_REQUESTED_TEXT,
} from '@/lib/categoryNoFit';

// How long to wait after the description stops changing before firing the
// suggestion request - avoids one request per keystroke.
const SUGGEST_DEBOUNCE_MS = 400;

// Time-based (setInterval), not per-frame (requestAnimationFrame) - the
// scroll rate stays the same regardless of display refresh rate. ~10px
// every 60ms ≈ 165px/s, roughly a few rows per second at this list's
// typical row height.
const SCROLL_INTERVAL_MS = 60;
const SCROLL_STEP_PX = 10;

const CategoryScrollButton = ({ direction, viewportRef }) => {
  const timerRef = useRef(null);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    if (timerRef.current) return;
    timerRef.current = setInterval(() => {
      if (viewportRef.current) {
        viewportRef.current.scrollTop += direction === 'up' ? -SCROLL_STEP_PX : SCROLL_STEP_PX;
      }
    }, SCROLL_INTERVAL_MS);
  }, [direction, viewportRef]);

  useEffect(() => stop, [stop]);

  return (
    <div
      className="flex shrink-0 cursor-default items-center justify-center py-1 bg-popover text-popover-foreground"
      onMouseEnter={start}
      onMouseLeave={stop}
      data-testid={`category-scroll-${direction}`}
    >
      {direction === 'up' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
    </div>
  );
};

const CategoryOption = ({ cat }) => (
  <SelectItem key={cat.custom_code || cat.id} value={cat.custom_code} className="py-2">
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs bg-zinc-100 px-1.5 py-0.5 rounded">{cat.custom_code}</span>
        <span
          className={`text-[10px] px-1.5 py-0.5 rounded ${
            cat.flag === 'Goods' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
          }`}
        >
          {cat.flag}
        </span>
      </div>
      <span className="text-sm font-medium">{cat.category_name}</span>
      {cat.l2_subcategory && (
        <span className="text-xs text-zinc-500">
          {cat.l1_category} → {cat.l2_subcategory}
        </span>
      )}
    </div>
  </SelectItem>
);

const CategorySelectCell = ({
  value,
  onChange,
  description,
  index,
  lineItems,
  categories,
  loadingCategories,
  assessedCategoryCode,
  fieldError,
  // NO-FIT (F1 / C1b-1b, A2). Optional, and absent by default: a surface
  // that has not been wired for the declaration renders exactly what it
  // rendered before, rather than showing a control that writes nowhere -
  // the same defensive convention RequirementContextCard already applies
  // to onCategoryConfirm.
  noFitSubject = null,
  onDeclareNoFit = null,
  onClearNoFit = null,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [categorySearch, setCategorySearch] = useState('');
  // Whether the free-text capture is open, and what is in it. Local: an
  // abandoned request must leave nothing behind, so closing this control
  // writes nothing at all (same rule as CategoryChangeControl's Cancel -
  // the absence of a decision must never be recorded as one).
  const [requesting, setRequesting] = useState(false);
  const [requestText, setRequestText] = useState('');
  const debounceRef = useRef(null);
  const viewportRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const text = (description || '').trim();
    if (!text) {
      setSuggestions([]);
      return undefined;
    }
    debounceRef.current = setTimeout(() => {
      api
        .suggestCategoriesForLineItem(text)
        .then((response) => setSuggestions(response.data || []))
        .catch(() => setSuggestions([]));
    }, SUGGEST_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [description]);

  const usedCodes = useMemo(() => usedCategoryCodes(lineItems, index), [lineItems, index]);
  const tiers = useMemo(
    () => buildCategoryTiers({ categories, suggestions, usedCodes, search: categorySearch }),
    [categories, suggestions, usedCodes, categorySearch]
  );
  const consistencyWarning = useMemo(
    () =>
      getCategoryConsistencyWarning({
        item: lineItems[index],
        index,
        lineItems,
        categories,
        assessedCategoryCode,
      }),
    [lineItems, index, categories, assessedCategoryCode]
  );

  const noResultsAtAll = tiers.suggested.length === 0 && tiers.used.length === 0 && tiers.all.length === 0;

  // The record is read through THE shared reader, never by poking at the
  // key - so this surface, the save-arm exemption, the forward refusal and
  // the admin query cannot disagree about what counts as a declaration.
  const noFitRecord = readCategoryNoFit(noFitSubject);
  const noFitRequestNumber = categoryNoFitRequestNumber(noFitSubject);
  const noFitControlEnabled = Boolean(onDeclareNoFit);

  // A NO-FIT IS A DECLARATION, NOT A LOCK (Ashok ruling). Reselecting is
  // the buyer's route out - and the ONLY route out of a declined request,
  // since a disposition never unblocks them. Clearing lives HERE rather
  // than at each call site so all three pickers behave identically.
  const handleSelect = (next) => {
    if (noFitRecord && onClearNoFit) onClearNoFit();
    onChange(next);
  };

  const closeRequest = () => {
    setRequesting(false);
    setRequestText('');
  };

  const submitRequest = () => {
    const text = requestText.trim();
    // REQUIRED TEXT, guarded at the control as well as at the writer: a
    // declaration with nothing in it reaches the admin as a row saying
    // only that somebody wanted something.
    if (!text) return;
    onDeclareNoFit(text);
    closeRequest();
  };

  return (
    <>
      <Select value={value} onValueChange={handleSelect}>
        <SelectTrigger
          className={`h-8 text-sm bg-white min-w-[180px] ${fieldError ? 'border-red-500 ring-1 ring-red-500' : ''}`}
          data-testid={`category-select-trigger-${index}`}
        >
          {/* The parked sentence reaches the trigger too, so a collapsed
              row still says what state it is in - "Select Category" on a
              line the buyer has already declared is a lie about their own
              document. Read from the one place the wording lives. */}
          <SelectValue placeholder={noFitRecord ? AWAITING_MASTER_UPDATE : 'Select Category'}>
            {value &&
              (() => {
                const cat = categories.find((c) => c.custom_code === value);
                return cat ? (
                  // The SAME renderer the Ship To trigger and option list
                  // now use (2026-08-23) - extracted verbatim, markup
                  // unchanged, so this trigger renders byte-identically to
                  // what it rendered before.
                  <CodeNameLabel code={cat.custom_code} name={cat.category_name} />
                ) : (
                  <span className="truncate text-xs flex items-center gap-1 text-amber-600">
                    <AlertCircle className="w-3 h-3" />
                    {value} (update required)
                  </span>
                );
              })()}
          </SelectValue>
        </SelectTrigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position="popper"
            className="relative z-50 flex flex-col max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-select-content-transform-origin] data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1"
          >
            {/* Sticky search - a plain non-scrolling sibling above the
                scrollable viewport (see file header). */}
            <div className="px-2 py-1.5 border-b shrink-0" data-testid="category-search-sticky-header">
              <Input
                placeholder="Search categories..."
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                className="h-7 text-xs"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <CategoryScrollButton direction="up" viewportRef={viewportRef} />
            <SelectPrimitive.Viewport
              ref={viewportRef}
              className="flex-1 min-h-0 overflow-y-auto p-1 w-full min-w-[var(--radix-select-trigger-width)]"
            >
              {loadingCategories ? (
                <SelectItem value="__loading__" disabled>
                  Loading categories...
                </SelectItem>
              ) : noResultsAtAll ? (
                <div className="px-2 py-3 text-xs text-zinc-500 text-center">
                  {categorySearch ? 'No matching categories' : 'No categories available. Upload via Master Data.'}
                </div>
              ) : (
                <>
                  {tiers.used.length > 0 && (
                    <SelectGroup>
                      <SelectLabel className="text-[10px] uppercase tracking-wide text-zinc-400">Used in this RFP</SelectLabel>
                      {tiers.used.map((cat) => (
                        <CategoryOption key={`used-${cat.custom_code}`} cat={cat} />
                      ))}
                    </SelectGroup>
                  )}
                  {tiers.suggested.length > 0 && (
                    <SelectGroup>
                      <SelectLabel className="text-[10px] uppercase tracking-wide text-zinc-400">Suggested</SelectLabel>
                      {tiers.suggested.map((cat) => (
                        <CategoryOption key={`suggested-${cat.custom_code}`} cat={cat} />
                      ))}
                    </SelectGroup>
                  )}
                  <SelectGroup>
                    <SelectLabel className="text-[10px] uppercase tracking-wide text-zinc-400">All categories</SelectLabel>
                    {tiers.all.map((cat) => (
                      <CategoryOption key={`all-${cat.custom_code}`} cat={cat} />
                    ))}
                  </SelectGroup>
                </>
              )}
            </SelectPrimitive.Viewport>
            <CategoryScrollButton direction="down" viewportRef={viewportRef} />
            {/* ================================================================
                THE SIBLING CONTROL (F1 / C1b-1b, A2)
                ================================================================
                A SIBLING, NEVER A DROPDOWN ROW (Ashok ruling 5). It is not a
                SelectItem and it is not inside the Viewport, so it can never
                be selected as if it were a category - "none of these fit" is
                not one of the things that fit.

                AND IT SITS OUTSIDE THE THREE-WAY TERNARY ABOVE, which is the
                whole point. That ternary's noResultsAtAll branch renders a
                plain <div> - "No matching categories" - and a control placed
                inside it would be inert, while a control placed inside the
                normal branch would vanish at exactly the moment a buyer has
                searched, found nothing, and concluded that nothing fits. That
                moment is what this control exists for, so it renders in ALL
                THREE branches, loading included.

                Structurally this is the same slot the sticky search header
                occupies at the top of the Content: a non-scrolling sibling of
                the Viewport. That header is the working precedent that an
                interactive non-item element survives here. */}
            {noFitControlEnabled && (
              <div
                className="border-t shrink-0 px-2 py-2 bg-popover text-popover-foreground"
                data-testid={`category-no-fit-control-${index}`}
                onClick={(e) => e.stopPropagation()}
              >
                {noFitRecord ? (
                  <div className="text-xs space-y-0.5" data-testid={`category-no-fit-declared-${index}`}>
                    <p className="font-medium text-amber-700">{AWAITING_MASTER_UPDATE}</p>
                    <p className="text-zinc-600 break-words">
                      Requested: {noFitRecord[NO_FIT_FIELD_REQUESTED_TEXT]}
                    </p>
                    {noFitRequestNumber && (
                      <p className="text-zinc-500 font-mono" data-testid={`category-no-fit-number-${index}`}>
                        {noFitRequestNumber}
                      </p>
                    )}
                    <p className="text-zinc-500">
                      Selecting a category above withdraws this request.
                    </p>
                  </div>
                ) : requesting ? (
                  <div className="space-y-1.5">
                    <label
                      className="block text-[10px] uppercase tracking-wide text-zinc-500"
                      htmlFor={`category-no-fit-text-${index}`}
                    >
                      Describe the category you need
                    </label>
                    <Input
                      id={`category-no-fit-text-${index}`}
                      value={requestText}
                      onChange={(e) => setRequestText(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      // Radix Select runs typeahead off keydown at the
                      // content level; the search box above gets away
                      // without this because its own keystrokes are
                      // harmless, but a request sentence typed into a
                      // dropdown that is jumping to matching options is
                      // not. Stopped here rather than in the shared
                      // primitive, same rationale as the rest of this
                      // custom content shell.
                      onKeyDown={(e) => e.stopPropagation()}
                      placeholder="e.g. Unmanned aerial survey services"
                      className="h-7 text-xs"
                      data-testid={`category-no-fit-text-${index}`}
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={submitRequest}
                        disabled={!requestText.trim()}
                        className="text-xs font-medium text-white bg-[#0055FF] hover:bg-[#0044CC] disabled:bg-zinc-300 disabled:text-zinc-500 rounded px-2 py-1"
                        data-testid={`category-no-fit-submit-${index}`}
                      >
                        Send request
                      </button>
                      {/* CANCEL WRITES NOTHING - no request, no state, no
                          declaration. Same rule as CategoryChangeControl's
                          own Cancel (DATAFLOW Principle 16). */}
                      <button
                        type="button"
                        onClick={closeRequest}
                        className="text-xs text-zinc-500 underline hover:text-zinc-700"
                        data-testid={`category-no-fit-cancel-${index}`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setRequesting(true)}
                    className="w-full text-left text-xs text-zinc-600 underline hover:text-zinc-900"
                    data-testid={`category-no-fit-open-${index}`}
                  >
                    None of these fit - request a category
                  </button>
                )}
              </div>
            )}
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </Select>
      <FieldError message={fieldError} />
      {noFitRecord && (
        <p
          className="text-[10px] text-amber-700 mt-0.5 leading-tight"
          data-testid={`category-no-fit-badge-${index}`}
        >
          {AWAITING_MASTER_UPDATE}
          {noFitRequestNumber ? ` (${noFitRequestNumber})` : ''}
        </p>
      )}
      {consistencyWarning && (
        <p className="text-[10px] text-amber-600 mt-0.5 leading-tight" data-testid={`category-consistency-warning-${index}`}>
          {consistencyWarning.message}
        </p>
      )}
    </>
  );
};

export default CategorySelectCell;
